import { inject, Injectable } from '@angular/core';

import { Observable, of } from 'rxjs';
import { map, mapTo, switchMap } from 'rxjs/operators';

import { HttpHeaders, HttpParams } from '@angular/common/http';
import { LocaleCode } from '@language';
import {
    Group,
    GroupId,
    Organisation,
    OrganisationId,
    OrganisationStatistics,
    Role,
    ShAbstractHttpService,
    ShPaginationService,
    UserPreferences,
    UserRights,
    Zone,
    ZoneId
} from '@stylehaven/components';
import { ViewOrganisationsGuard } from '../../guards/view-organisations-guard.service';

@Injectable({ providedIn: 'root' })
export class MediaHavenOrganisationService {
    private readonly organisationEndpoint: string = 'v2/organisations';

    private readonly paginationService: ShPaginationService = inject(ShPaginationService);
    private readonly http: ShAbstractHttpService = inject(ShAbstractHttpService);
    private orgGuard: ViewOrganisationsGuard = inject(ViewOrganisationsGuard);

    // #region GET organisation

    public getOrganisations(): Observable<Organisation[]> {
        const organisations$: Observable<Organisation[]> = this.paginationService
            .getAll<Organisation>(this.organisationEndpoint, new HttpParams(), new HttpHeaders())
            .pipe(
                map(organisations =>
                    organisations.map(organisation =>
                        organisation.LongName ? organisation : { ...organisation, LongName: organisation.Name }
                    )
                )
            );
        return this.orgGuard.hasAccess().pipe(switchMap(canAccess => (canAccess ? organisations$ : of([]))));
    }

    public getOrganisationById(id: OrganisationId): Observable<Organisation> {
        return this.findOrgInCache(id.toString()).pipe(
            switchMap(organisation =>
                organisation ? of(organisation) : this.http.get<Organisation>(`${this.organisationEndpoint}/${id}`)
            )
        );
    }

    public getStatistics(id: OrganisationId): Observable<OrganisationStatistics> {
        return this.http.get(`${this.organisationEndpoint}/${id}/stats`);
    }

    // #endregion

    // #region PUT organisation
    public updateOrganisationById(orgId: string, organisation: Organisation): Observable<boolean> {
        const request = `${this.organisationEndpoint}/${orgId}`;
        return this.http.put(request, organisation).pipe(mapTo(true));
    }
    // #endregion

    // #region GET Defaults
    public getDefaultRoles(organisationId: string): Observable<Role[]> {
        const request = `${this.organisationDefaultEndpoint(organisationId)}/roles`;
        return this.paginationService
            .get<Role>(request, new HttpParams(), new HttpHeaders())
            .pipe(map(paginatedResponse => paginatedResponse.Results));
    }

    public getDefaultRights(organisationId: string): Observable<UserRights> {
        const request = `${this.organisationDefaultEndpoint(organisationId)}/rights`;
        return this.http.get<UserRights>(request);
    }

    public getDefaultLocale(organisationId: string): Observable<string> {
        const request = `${this.organisationDefaultEndpoint(organisationId)}/locale`;
        return this.http.getText<string>(request);
    }

    public getDefaultPreferences(organisationId: string): Observable<UserPreferences> {
        const request = `${this.organisationDefaultEndpoint(organisationId)}/preferences`;
        return this.http.get<UserPreferences>(request);
    }

    public getDefaultZones(organisationId: string): Observable<Zone[]> {
        const request = `${this.organisationDefaultEndpoint(organisationId)}/zones`;
        return this.paginationService
            .get<Zone>(request, new HttpParams(), new HttpHeaders())
            .pipe(map(paginatedResponse => paginatedResponse.Results));
    }

    public getDefaultGroups(organisationId: string): Observable<Group[]> {
        const request = `${this.organisationDefaultEndpoint(organisationId)}/groups`;
        return this.paginationService
            .get<Group>(request, new HttpParams(), new HttpHeaders())
            .pipe(map(paginatedResponse => paginatedResponse.Results));
    }

    // #endregion

    // #region PUT defaults
    public updateDefaultRoles(orgId: string, roleIds: string[]): Observable<boolean> {
        const request = `${this.organisationDefaultEndpoint(orgId)}/roles`;
        return this.http.put(request, roleIds).pipe(mapTo(true));
    }

    public updateDefaultRights(orgId: string, rights: UserRights): Observable<boolean> {
        const request = `${this.organisationDefaultEndpoint(orgId)}/rights`;
        return this.http.put(request, rights).pipe(mapTo(true));
    }

    public updateDefaultLocale(orgId: string, locale: LocaleCode): Observable<boolean> {
        const request = `${this.organisationDefaultEndpoint(orgId)}/locale`;
        return this.http.put(request, locale).pipe(mapTo(true));
    }

    public updateDefaultPreferences(orgId: string, prefs: UserPreferences): Observable<boolean> {
        const request = `${this.organisationDefaultEndpoint(orgId)}/preferences`;
        return this.http.put(request, prefs).pipe(mapTo(true));
    }

    public updateDefaultZones(orgId: string, zones: ZoneId[]): Observable<boolean> {
        const request = `${this.organisationDefaultEndpoint(orgId)}/zones`;
        return this.http.put(request, zones).pipe(mapTo(true));
    }

    public updateDefaultGroups(orgId: string, groups: GroupId): Observable<boolean> {
        const request = `${this.organisationDefaultEndpoint(orgId)}/groups`;
        return this.http.put(request, groups).pipe(mapTo(true));
    }
    // #endregion

    private organisationDefaultEndpoint(organisationId: string): string {
        return `${this.organisationEndpoint}/${organisationId}/defaults`;
    }

    private findOrgInCache(organisationId: string): Observable<Organisation | undefined> {
        return this.getOrganisations().pipe(
            map(organisations => organisations.find(org => org.Id.toString() === organisationId))
        );
    }
}
